import { ArrowDown, ArrowRight, type LucideIcon } from "lucide-react";
import { Fragment } from "react";

export interface GameJourneyStep {
  icon: LucideIcon;
  title: string;
  text: string;
}

export interface GameJourneyLane {
  // Short label over the lane ("The customer journey", "Before payment...").
  tag: string;
  steps: GameJourneyStep[];
}

interface GameJourneyProps {
  lanes: GameJourneyLane[];
  ariaLabel: string;
}

// The Website 2.0 "game journey" (preview visuals game_journey_wheel and
// game_journey_scratch): one white lane per journey, each a row of gradient
// icon steps joined by a round arrow badge, level with the icons. On phones
// the steps stack and the badge points down; the lane tag is centred there
// to match.
const GameJourney = ({ lanes, ariaLabel }: GameJourneyProps) => (
  <figure role="group" aria-label={ariaLabel} className="grid gap-[18px]">
    {lanes.map((lane) => (
      <div
        key={lane.tag}
        className="grid gap-3.5 rounded-[26px] bg-white px-[22px] pt-5 pb-[22px] shadow-card"
      >
        <span className="justify-self-center rounded-full bg-tone-blush md:justify-self-start px-3.5 py-1.5 text-[12.5px] font-extrabold tracking-[0.07em] text-primary uppercase">
          {lane.tag}
        </span>
        <ol className="grid md:flex md:items-start">
          {lane.steps.map((step, index) => (
            <Fragment key={step.title}>
              {index > 0 && (
                <li
                  aria-hidden="true"
                  className="flex items-center justify-center py-2 md:h-[62px] md:w-14 md:shrink-0 md:py-0"
                >
                  <span className="grid size-8 shrink-0 place-items-center rounded-full bg-tone-blush text-primary ring-4 ring-white">
                    <ArrowDown className="size-4 md:hidden" strokeWidth={2.5} />
                    <ArrowRight className="hidden size-4 md:block" strokeWidth={2.5} />
                  </span>
                </li>
              )}
              <li className="grid justify-items-center gap-1.5 text-center md:flex-1">
                <span className="grid size-[62px] place-items-center rounded-[20px] bg-brand-gradient text-white shadow-card">
                  <step.icon
                    className="size-7"
                    strokeWidth={1.8}
                    aria-hidden="true"
                  />
                </span>
                <span className="text-[1.05rem] font-bold">{step.title}</span>
                <p className="max-w-[24ch] text-[14.5px] leading-[1.45] text-ink-muted">
                  {step.text}
                </p>
              </li>
            </Fragment>
          ))}
        </ol>
      </div>
    ))}
  </figure>
);

export default GameJourney;
